import {
  MouseEventHandler,
  ChangeEvent,
  KeyboardEventHandler,
  RefObject,
} from "react";
import { RefCallBack } from "react-hook-form";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import Icon from "../Icon";
import { isEmpty } from "../../../core/helpers/helpers";

import styles from "./Input.module.css";

type RenderFunction = () => JSX.Element;
type OnChangeFunction = (
  e: ChangeEvent<HTMLInputElement>,
  value: string,
) => void;

export interface TextInputProps {
  type?: "text" | "password" | "number" | "email" | "search";
  inputRef?: RefObject<HTMLInputElement> | RefCallBack;
  label?: string;
  iconLabel?: string;
  hideLabel?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  description?: string;
  validationError?: string;
  withValidationMessage?: boolean;
  invalid?: boolean;
  value?: string;
  id?: string;
  onChange?: OnChangeFunction;
  onBlur?: OnChangeFunction;
  width?: string | number;
  // icon
  renderIcon?: RenderFunction;
  iconPosition?: "left" | "right";
  onIconClick?: MouseEventHandler<HTMLSpanElement>;

  // text pinned to the input (left or right)
  pinnedText?: string;
  pinnedTextPosition?: "left" | "right";

  // number input specific props
  min?: number;
  max?: number;
  step?: number;
  enableNumberArrows?: boolean;
  autoComplete?: string;
  disableBrowserAutocomplete?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  dataCy?: string;
  dataCyBtn?: string;
  css?: any;
  autoFocus?: boolean;
  name?: string;
  dataPrTooltip?: string;
  dataPrAt?: string;
  className?: string;
  inputClassName?: string;
}

/** Represents <input /> tag */
export const TextInput = ({
  type = "text",
  inputRef = undefined,
  label = "",
  iconLabel = "text input icon",
  hideLabel = false,
  placeholder = "",
  required = false,
  disabled = false,
  description = "",
  validationError = undefined,
  withValidationMessage = true,
  invalid = false,
  value = "",
  onChange = () => {},
  onBlur = () => {},
  width,
  renderIcon = undefined,
  iconPosition = "right",
  onIconClick = undefined,
  pinnedText = undefined,
  pinnedTextPosition = undefined,
  min = undefined,
  max = undefined,
  step = undefined,
  enableNumberArrows = false,
  autoComplete = "on",
  id = uuidv4(),
  onKeyDown,
  dataCy,
  dataCyBtn,
  css,
  autoFocus,
  name,
  dataPrTooltip,
  dataPrAt,
  className,
  inputClassName,
  disableBrowserAutocomplete,
}: TextInputProps) => {
  const internalValue = value ?? undefined;
  const resolvedAutoComplete = disableBrowserAutocomplete
    ? "new-password"
    : autoComplete;

  return (
    <div className={classNames(styles.inputContainer, className)}>
      {/* label row */}
      <div
        className={classNames({
          [styles.labelRow]: !hideLabel && !isEmpty(label),
          visuallyHidden: hideLabel || isEmpty(label),
        })}
      >
        {required && <span className={styles.requiredIcon}>*</span>}
        {label && <label htmlFor={id}>{label}</label>}
      </div>

      {/* input row */}
      <div
        className={classNames({
          [styles.inputRow]: true,
          [styles.filledBorder]: !!value,
          [styles.inputRowIconLeft]: renderIcon && iconPosition === "left",
          [styles.inputRowIconRight]: renderIcon && iconPosition === "right",
          [styles.inputRowPinnedTextLeft]:
            pinnedText && pinnedTextPosition === "left",
          [styles.inputRowPinnedTextRight]:
            pinnedText && pinnedTextPosition === "right",
        })}
        style={{ width }}
      >
        {pinnedText ? (
          <span
            className={classNames({
              [styles.inputPinnedText]: true,
              [styles.inputPinnedTextLeft]: pinnedTextPosition === "left",
              [styles.inputPinnedTextRight]: pinnedTextPosition === "right",
              [styles.inputPinnedTextDisabled]: disabled,
            })}
          >
            {pinnedText}
          </span>
        ) : null}

        <input
          aria-label={label || placeholder}
          name={name}
          autoFocus={autoFocus}
          data-cy={dataCy}
          style={css}
          ref={inputRef}
          id={id}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          value={internalValue}
          autoComplete={resolvedAutoComplete}
          onChange={(e) => {
            onChange(e, e.target.value);
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e);
          }}
          onBlur={(e) => {
            onBlur(e, e.target.value);
          }}
          className={classNames(inputClassName, {
            [styles.inputValidationError]: invalid || !!validationError,
            [styles.disableNumberArrows]: !enableNumberArrows,
          })}
          min={min}
          max={max}
          step={step}
          data-pr-tooltip={dataPrTooltip}
          data-pr-at={dataPrAt}
        />

        {/* icon */}
        {renderIcon && !pinnedText ? (
          <button
            type="button"
            tabIndex={internalValue?.length ? 0 : -1}
            data-cy={dataCyBtn}
            disabled={!onIconClick}
            className={classNames({
              [styles.inputIcon]: true,
              [styles.inputIconLeft]: iconPosition === "left",
              [styles.inputIconRight]: iconPosition === "right",
              [styles.inputIconClickable]: !!onIconClick,
              [styles.inputIconDisabled]: disabled,
            })}
            onClick={onIconClick}
            aria-label={iconLabel}
          >
            {renderIcon()}
          </button>
        ) : null}
      </div>

      {/* Other */}
      {validationError && withValidationMessage ? (
        <div data-cy="validation_error" className={styles.validationError}>
          <Icon name="exclamation-circle" size={14} />
          {validationError}
        </div>
      ) : null}
      <p className={classNames("description", { ["ml-16"]: validationError })}>
        {description}
      </p>
    </div>
  );
};
export default TextInput;
